import React, { useState, useEffect, useRef, useMemo } from "react";

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export default function DatePickerPopover({ selectedDate, setSelectedDate }) {
  const [isOpen, setIsOpen] = useState(false);
  
  // Parse initial selectedDate safely to local time
  const parsedDate = useMemo(() => {
    if (!selectedDate) return new Date();
    const [yr, mt, dy] = selectedDate.split("-").map(Number);
    return new Date(yr, mt - 1, dy);
  }, [selectedDate]);

  // Calendar view state (which month/year are we currently viewing in the calendar grid)
  const [viewMonth, setViewMonth] = useState(parsedDate.getMonth());
  const [viewYear, setViewYear] = useState(parsedDate.getFullYear());

  const containerRef = useRef(null);

  // Sync calendar view when selectedDate changes from outside (or when opened)
  useEffect(() => {
    setViewMonth(parsedDate.getMonth());
    setViewYear(parsedDate.getFullYear());
  }, [parsedDate, isOpen]);

  // Click outside listener
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    
    // Escape key listener
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const handleSelectDay = (day, offset) => {
    let targetYear = viewYear;
    let targetMonth = viewMonth + offset;

    if (targetMonth < 0) {
      targetMonth = 11;
      targetYear -= 1;
    } else if (targetMonth > 11) {
      targetMonth = 0;
      targetYear += 1;
    }

    const yearStr = targetYear;
    const monthStr = String(targetMonth + 1).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");
    
    setSelectedDate(`${yearStr}-${monthStr}-${dayStr}`);
    setIsOpen(false);
  };

  const handleGoToToday = () => {
    const today = new Date();
    const yearStr = today.getFullYear();
    const monthStr = String(today.getMonth() + 1).padStart(2, "0");
    const dayStr = String(today.getDate()).padStart(2, "0");
    setSelectedDate(`${yearStr}-${monthStr}-${dayStr}`);
    setIsOpen(false);
  };

  // Generate calendar cells (42 total)
  const cells = useMemo(() => {
    const startDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();

    const result = [];

    // Prev month days
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      result.push({
        day: prevMonthDays - i,
        isCurrentMonth: false,
        offset: -1,
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      result.push({
        day: i,
        isCurrentMonth: true,
        offset: 0,
      });
    }

    // Next month days
    const nextDaysCount = 42 - result.length;
    for (let i = 1; i <= nextDaysCount; i++) {
      result.push({
        day: i,
        isCurrentMonth: false,
        offset: 1,
      });
    }

    return result;
  }, [viewMonth, viewYear]);

  const todayStr = useMemo(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  }, []);

  return (
    <div className="position-relative" ref={containerRef} style={{ zIndex: isOpen ? 999 : "auto" }}>
      {/* Trigger Button */}
      <div 
        className="shadow-sm d-flex align-items-center justify-content-center transition-all"
        style={{ 
          width: "48px", 
          height: "48px", 
          cursor: "pointer", 
          backgroundColor: isOpen ? "#ffffff" : "#fffbf5",
          border: isOpen ? "2px solid #ea580c" : "2px solid #4a2c11",
          borderRadius: "16px",
          boxShadow: "0 8px 24px rgba(74, 44, 17, 0.08)",
          transition: "all 0.2s ease"
        }}
        title="Alterar data do Dashboard"
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.backgroundColor = "#ffffff";
            e.currentTarget.style.borderColor = "#ea580c";
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.backgroundColor = "#fffbf5";
            e.currentTarget.style.borderColor = "#4a2c11";
          }
        }}
      >
        <i className="mdi mdi-calendar-month" style={{ fontSize: "22px", color: "#ea580c" }}></i>
      </div>

      {/* Popover Custom Calendar */}
      {isOpen && (
        <div 
          className="position-absolute mt-2"
          style={{
            right: 0,
            top: "52px",
            width: "320px",
            backgroundColor: "#ffffff",
            border: "2px solid #4a2c11",
            borderRadius: "16px",
            boxShadow: "0 15px 40px rgba(74, 44, 17, 0.15)",
            padding: "16px",
            fontFamily: "'Outfit', 'Inter', sans-serif"
          }}
        >
          {/* Calendar Header */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <button 
              type="button"
              className="btn btn-sm d-flex align-items-center justify-content-center p-0"
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                backgroundColor: "#fffbf5",
                border: "1.5px solid #ebdcc5",
                color: "#ea580c",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#ea580c";
                e.currentTarget.style.color = "#ffffff";
                e.currentTarget.style.borderColor = "#ea580c";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#fffbf5";
                e.currentTarget.style.color = "#ea580c";
                e.currentTarget.style.borderColor = "#ebdcc5";
              }}
              onClick={handlePrevMonth}
            >
              <i className="mdi mdi-chevron-left" style={{ fontSize: "20px" }}></i>
            </button>

            <span style={{ 
              fontWeight: "900", 
              color: "#4a2c11", 
              fontSize: "15px",
              textTransform: "capitalize"
            }}>
              {MONTHS[viewMonth]} {viewYear}
            </span>

            <button 
              type="button"
              className="btn btn-sm d-flex align-items-center justify-content-center p-0"
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                backgroundColor: "#fffbf5",
                border: "1.5px solid #ebdcc5",
                color: "#ea580c",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#ea580c";
                e.currentTarget.style.color = "#ffffff";
                e.currentTarget.style.borderColor = "#ea580c";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#fffbf5";
                e.currentTarget.style.color = "#ea580c";
                e.currentTarget.style.borderColor = "#ebdcc5";
              }}
              onClick={handleNextMonth}
            >
              <i className="mdi mdi-chevron-right" style={{ fontSize: "20px" }}></i>
            </button>
          </div>

          {/* Weekdays Grid */}
          <div className="d-grid mb-2" style={{ gridTemplateColumns: "repeat(7, 1fr)" }}>
            {WEEKDAYS.map((day) => (
              <div 
                key={day} 
                className="text-center" 
                style={{ 
                  fontWeight: "800", 
                  fontSize: "12px", 
                  color: "#784b22",
                  paddingBottom: "4px"
                }}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Days Grid */}
          <div className="d-grid gap-1" style={{ gridTemplateColumns: "repeat(7, 1fr)" }}>
            {cells.map((cell, index) => {
              // Construct actual cell date string to check if selected or today
              let cellYear = viewYear;
              let cellMonth = viewMonth + cell.offset;
              if (cellMonth < 0) {
                cellMonth = 11;
                cellYear -= 1;
              } else if (cellMonth > 11) {
                cellMonth = 0;
                cellYear += 1;
              }

              const cellDateStr = `${cellYear}-${String(cellMonth + 1).padStart(2, "0")}-${String(cell.day).padStart(2, "0")}`;
              const isSelected = cellDateStr === selectedDate;
              const isToday = cellDateStr === todayStr;

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSelectDay(cell.day, cell.offset)}
                  style={{
                    border: isToday ? "2px solid #ea580c" : "2px solid transparent",
                    borderRadius: "8px",
                    height: "36px",
                    fontSize: "13px",
                    fontWeight: isSelected || isToday ? "700" : "500",
                    background: isSelected && !isToday
                      ? "linear-gradient(135deg, #ea580c 0%, #784b22 100%)" 
                      : "transparent",
                    color: isSelected && !isToday 
                      ? "#ffffff" 
                      : isToday 
                        ? "#ea580c" 
                        : cell.isCurrentMonth 
                          ? "#4a2c11" 
                          : "#c3b3a3",
                    outline: "none",
                    position: "relative",
                    transition: "all 0.15s ease",
                    cursor: "pointer",
                    boxShadow: isSelected && !isToday ? "0 4px 10px rgba(234, 88, 12, 0.25)" : "none"
                  }}
                  onMouseEnter={(e) => {
                    if (!(isSelected && !isToday)) {
                      e.currentTarget.style.backgroundColor = "#fffbf5";
                      e.currentTarget.style.color = "#ea580c";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(isSelected && !isToday)) {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = isToday 
                        ? "#ea580c" 
                        : (cell.isCurrentMonth ? "#4a2c11" : "#c3b3a3");
                    }
                  }}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>

          {/* Quick Footer Action Bar */}
          <div className="border-top mt-3 pt-3 d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-sm py-1.5 px-3"
              style={{
                fontFamily: "'Outfit', 'Inter', sans-serif",
                fontWeight: "900",
                fontSize: "12px",
                color: "#4a2c11",
                backgroundColor: "#fffbf5",
                border: "1.5px solid #ebdcc5",
                borderRadius: "8px",
                transition: "all 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#ea580c";
                e.currentTarget.style.color = "#ea580c";
                e.currentTarget.style.backgroundColor = "#ffffff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#ebdcc5";
                e.currentTarget.style.color = "#4a2c11";
                e.currentTarget.style.backgroundColor = "#fffbf5";
              }}
              onClick={handleGoToToday}
            >
              Ir para Hoje
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
